import {
  BorrowerDrillDownQuery,
  BorrowerRow,
  SalesInventorySummary,
  SalesInventoryView,
  TeamSummary,
} from 'ng2-app/models/sales-inventory/sales-inventory.model';

// ---------------------------------------------------------------------------
// Summary state (metric cards + rep rows), cached per (view, teamId) scope
// ---------------------------------------------------------------------------

export interface SalesInventoryStateModel {
  /** Cached summaries keyed by scope (see salesInventorySummaryKey). */
  summaries: Record<string, SalesInventorySummary>;
  /** The scope key the UI is currently viewing. */
  currentKey: string | null;
  loading: boolean;
  fetching: boolean;
  /** Per-scope last-fetch timestamps for TTL invalidation. */
  lastSync: Record<string, Date>;
}

/** TTL for a cached summary scope. */
export const SALES_INVENTORY_SUMMARY_TTL = 1000 * 60 * 5;

/** Cache key for a summary scope. `teamId=null` and `view=null` (api-resolved) are their own keys. */
export function salesInventorySummaryKey(
  view: SalesInventoryView | null,
  teamId: number | null
): string {
  return `${view ?? 'auto'}:${teamId ?? 'default'}`;
}

// ---------------------------------------------------------------------------
// Team summary state (By-Team metric cards + team rows), cached per team-set scope
// ---------------------------------------------------------------------------

export interface TeamSummaryStateModel {
  /** Cached team summaries keyed by scope (see teamSummaryKey). */
  summaries: Record<string, TeamSummary>;
  /** The scope key the UI is currently viewing. */
  currentKey: string | null;
  loading: boolean;
  fetching: boolean;
  /** Per-scope last-fetch timestamps for TTL invalidation. */
  lastSync: Record<string, Date>;
}

/**
 * Cache key for a team-summary scope: the requested team ids, order-independent (a
 * caller re-ordering the same multi-select selection must hit the same cache entry).
 */
export function teamSummaryKey(teamIds: number[]): string {
  return [...teamIds].sort((a, b) => a - b).join(',');
}

// ---------------------------------------------------------------------------
// Borrower drill-down state (rows keyed by lenderUserId)
// ---------------------------------------------------------------------------

export interface SalesInventoryBorrowersStateModel {
  /** Drill-down rows keyed by borrowerDrillDownKey (lender user + full query). */
  borrowers: Record<string, BorrowerRow[]>;
  page: Record<string, { nextCursor: string | null, hasMore: boolean }>;
  loadingMore: Record<string, boolean>;
  loading: boolean;
  fetching: boolean;
  lastSync: Record<string, Date>;
}

/** TTL for a cached drill-down scope. */
export const SALES_INVENTORY_BORROWERS_TTL = 1000 * 60 * 5;

/**
 * Cache key for a drill-down. The result varies by lender user AND the full
 * query — view (AE vs ADR returns different rows) plus the status / quality /
 * stalledOnly / stage / flags / cadenceComplete filters, the column-header
 * filters (stages / cadenceDay / needs / timezone / callWindow), the lender / product
 * filters (lenderIds / productCategoryIds), the
 * assigned-date sort / range, the Max Offer range, and the free-text borrower
 * search — so all of them are part of the key. Any new query field that changes the returned rows must be
 * added here too, or cached results will leak across scopes. Changing the sort,
 * assigned range or search must reset the keyset cursor (a fresh first page),
 * which happens because a new key is a new cache scope. The search is gated to the
 * same >= 2 trimmed characters the service sends, so a sub-threshold term keys
 * identically to no search (the request is identical too).
 */
export function borrowerDrillDownKey(
  lenderUserId: number,
  query: BorrowerDrillDownQuery
): string {
  const status = (query.status ?? []).slice().sort().join('|');
  const flags = (query.flags ?? []).slice().sort().join('|');
  const stages = (query.stages ?? []).slice().sort().join('|');
  const cadenceDay = (query.cadenceDay ?? []).slice().sort().join('|');
  const needs = (query.needs ?? []).slice().sort().join('|');
  const timezone = (query.timezone ?? []).slice().sort().join('|');
  const callWindow = (query.callWindow ?? []).slice().sort().join('|');
  const lenderIds = (query.lenderIds ?? []).slice().sort((a, b) => a - b).join('|');
  const productCategoryIds = (query.productCategoryIds ?? []).slice().sort((a, b) => a - b).join('|');
  const searchTerm = query.search?.trim() ?? '';
  const search = searchTerm.length >= 2 ? searchTerm : '';
  return [
    lenderUserId,
    query.view,
    status,
    query.quality ?? '',
    query.stalledOnly ? 1 : 0,
    query.stage ?? '',
    stages,
    flags,
    query.cadenceComplete === undefined ? '' : (query.cadenceComplete ? 1 : 0),
    cadenceDay,
    needs,
    timezone,
    callWindow,
    query.sort ?? '',
    query.assignedFrom ?? '',
    query.assignedTo ?? '',
    query.maxOfferMin ?? '',
    query.maxOfferMax ?? '',
    query.noNoteDays ?? '',
    query.noCallDays ?? '',
    search,
    query.shared ?? '',
    lenderIds,
    productCategoryIds,
  ].join(':');
}

/**
 * Cache key for a team drill-down (By-Team mode): `team:` + teamId + the same folded query
 * as borrowerDrillDownKey, plus the sorted `repIds` filter. The prefix keeps a team id from
 * ever colliding with a lender-user id. An empty `repIds` keys the same as no filter.
 */
export function teamBorrowerDrillDownKey(teamId: number, query: BorrowerDrillDownQuery): string {
  const repIds = (query.repIds ?? []).slice().sort((a, b) => a - b).join('|');
  return `team:${borrowerDrillDownKey(teamId, query)}:${repIds}`;
}
